import type { MovingRect } from './canvasAlignment'
import { getInsertPlan, rowAfter, viewOf, worldOf, SPAWN_GAP, type InsertEntry, type InsertMove, type SpawnSide } from './canvasSpawn'
import type { SwapEntry } from './canvasSwap'
import { belongsToGroup, groupAround } from './canvasGroup'

/**
 * **Dazwischen schieben** (CONTEXT.md): Mit der Geste am **Tauschpunkt** vor
 * oder hinter einen Zettel einer Reihe. Hier steht, was beim Loslassen rückt;
 * welche **Ableg-Fläche** getroffen ist, rechnet `canvasSwapZones.ts`, die
 * Geste selbst liegt in `useSwapDrag`.
 */

export type ShoveAxis = 'x' | 'y'

export interface ShoveTarget {
  axis: ShoveAxis
  /** Der Bezug: Hinter ihm (*dahinter*) oder vor ihm (*davor*) wird eingereiht. */
  anchor: SwapEntry
  side: 'before' | 'after'
}

export interface ShovePlan {
  /** Wo das Getragene beim Loslassen liegt. */
  rect: MovingRect
  /** Schließen am Ursprung UND Öffnen am Ziel; wer steht, steht hier nicht. */
  moves: InsertMove[]
}

/** Das **Angebot**, wie es die Vorschau zeigt: wer ausweicht, wohin eingereiht wird, und die **Fuge**. */
export interface SpreadOffer {
  /** Wer ausweicht — jeder um die Weite des Spalts, in seine Richtung. */
  moves: InsertMove[]
  /** Die Lage, auf der eingereiht wird — die volle, nicht der Spalt. */
  rect: MovingRect
  /** Die Marke, mittig im Spalt. `null`, wo nichts aufgeht (Reihenende). */
  seam: SeamMark | null
}

/**
 * Die **Fuge** — die Marke, die das **Dazwischen schieben** vom **Platz
 * tauschen** unterscheidet (CONTEXT.md). Ein Strich quer zur Reihenachse,
 * mittig im SPALT, der aufgeht.
 *
 * **Mittig im Spalt und nicht auf der Lage, auf der eingereiht wird:** Der
 * Spalt ist so weit wie der **Rand**, auf dem der Zeiger steht — dieselbe
 * Strecke, die die Reihe ausweicht. Marke und Bewegung meinen damit
 * dieselbe Stelle, und die Marke sitzt dort, wo das Auge die Naht sieht.
 * (Bis 2026-09-04 ging die Reihe um drei Viertel einer Zettellänge auf und
 * die Marke stand mittig auf der künftigen Lage. Das war zweierlei: eine
 * weite Lücke hier, ein Strich dort.)
 */
export interface SeamMark {
  axis: ShoveAxis
  /** Der Anfang der Linie in Canvas-Koordinaten. */
  x: number
  y: number
  /** Ihre Länge — quer zur Reihenachse. */
  length: number
}

/**
 * Die **Fuge** an dieser Stelle, quer über die Lage, auf der eingereiht wird.
 *
 * **Einen halben Takt über jedes Ende hinaus** — nicht weiter: Sie liegt über
 * dem Papier, und was über die halbe Lücke hinausragt, liegt auf dem nächsten
 * Zettel (gemeldet 2026-09-04). In einem dichten Raster endet sie damit genau
 * in der Mitte der Lücke; in einem weiten hört sie früher auf und rührt
 * ebenfalls niemanden an.
 *
 * @param at die Stelle auf der REIHENACHSE — die Mitte des Spalts
 */
export function seamOf(rect: MovingRect, axis: ShoveAxis, at: number): SeamMark {
  const over = SPAWN_GAP / 2
  return axis === 'x'
    ? { axis, x: at, y: rect.y - over, length: rect.height + SPAWN_GAP }
    : { axis, x: rect.x - over, y: at, length: rect.width + SPAWN_GAP }
}

const lengthOf = (rect: MovingRect, axis: ShoveAxis) => (axis === 'x' ? rect.width : rect.height)

const samePlace = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5


/**
 * **Das Angebot**: Wer ausweicht, solange der Zeiger auf einem **Rand** steht
 * (CONTEXT.md, Tauschpunkt). Es rückt genau die Seite, auf die er zeigt.
 *
 * **So weit wie der Rand selbst** (`opening`), nicht so weit wie beim
 * Loslassen: Der Spalt, der aufgeht, ist damit genau die Fläche, auf der die
 * Hand steht — die Geste zeigt ihr eigenes Maß. Vorher ging die Reihe um drei
 * Viertel einer Zettellänge auf; das war eine weite Lücke ohne Aussage, und
 * die **Fuge** stand woanders als der Spalt (gemeldet 2026-09-04, „nur so
 * groß wie ihr Hover-Bereich"). Ein noch früherer Versuch mit einer
 * Randbreite las sich als „hier wackelt etwas" — damals gab es die Marke noch
 * nicht, die jetzt sagt, was der Spalt bedeutet.
 *
 * **Gezeigt wird, WER sich beim Loslassen rührt**, samt dem Schließen am
 * Ursprung — nur eben um ein kleineres Stück. Lange blieb das Schließen
 * draußen („eine Folge des Loslassens, keine Ankündigung"). Das log aber,
 * sobald Ursprung und Ziel in derselben Reihe liegen: Dort hebt das Schließen
 * das Öffnen ganz oder teilweise auf, und das Angebot schob Zettel beiseite,
 * die am Ende genau so stehen blieben, wie sie standen (gemeldet 2026-09-04:
 * drei nebeneinander, den ersten nach rechts). Jetzt gilt ohne Ausnahme: Wer
 * sich im Angebot rührt, rührt sich auch beim Loslassen — und in derselben
 * Reihe geht die Lücke eben von der anderen Seite auf.
 *
 * ⌘ bleibt außen vor: Es hält den Ursprung offen und wird erst beim Loslassen
 * gelesen. Das Getragene selbst rückt nie — es hängt am Zeiger.
 *
 * **Lage und Fuge kommen mit heraus**, obwohl sie nichts bewegen: Der Plan
 * rechnet die eine ohnehin, und die andere fällt aus den Rückenden ab. Beides
 * nach außen zu geben spart je Zeigerbewegung einen zweiten kompletten
 * Durchlauf, nur um zu erfahren, was drinnen schon dasteht.
 *
 * @param opening die Weite des Spalts in Canvas-Einheiten — die Breite des
 *                **Randes**, auf dem der Zeiger steht
 */
export function getSpreadPlan(
  entries: SwapEntry[],
  dragged: string,
  target: ShoveTarget,
  opening: number,
): SpreadOffer {
  const moved = entries.find(entry => entry.id === dragged)
  const others = entries.filter(entry => entry.id !== dragged)
  const anchor = others.find(entry => entry.id === target.anchor.id)
  const nothing = { moves: [], rect: moved?.rect ?? { x: 0, y: 0, width: 0, height: 0 }, seam: null }
  if (!moved || !anchor) return nothing

  const horizontal = target.axis === 'x'
  const start = new Map(others.map(entry => [entry.id, entry.rect]))
  const plan = getShovePlan(entries, dragged, target, true)
  /*
    **Wer NUR den Ursprung schließt, rückt schon die volle Strecke** — das
    geschieht hinter der Hand, nichts wandert unter ihr weg. Ein Ruck von
    einer Randbreite war dort von „nichts passiert" nicht zu unterscheiden,
    und beim Loslassen rückte die Reihe dann eine ganze Zettellänge (gemeldet
    2026-09-05). Am Ziel bleibt es beim kleinen Stück: Dort steht die Hand.

    **Nur, wenn der Ursprung in einer ANDEREN Reihe liegt.** Sortiert man in
    DERSELBEN Reihe um, ist das Schließen ja gerade die Bewegung, die den
    Spalt vor dem Zeiger aufgehen lässt — sie voll zu zeigen hieße, die Reihe
    um eine ganze Zettellänge zu öffnen, und genau das war 2026-09-04 als
    „weite Lücke ohne Aussage" gemeldet.

    Erkannt am Ergebnis, nicht an einer zweiten Liste: Wer im vollen Plan
    genau dort landet, wo ihn das Schließen ALLEIN hinsetzt, schließt nur.
  */
  const originRow = axisView(moved.rect, horizontal)
  const anchorRow = axisView(anchor.rect, horizontal)
  const sameRow = originRow.crossEnd > anchorRow.crossStart && originRow.crossStart < anchorRow.crossEnd
  const shutting = new Map(
    sameRow ? [] : closingOn(moved, others, target.axis).moves.map(move => [move.id, move]),
  )

  /*
    **Wo der Spalt aufgeht: zwischen denen, die weichen, und denen, die
    stehen.**

    Er geht in BEIDE Richtungen auf, je nachdem, woher der Getragene kommt.
    Kommt er von außen, rückt die Zielreihe vorwärts, und der Spalt liegt an
    der neuen führenden Kante des Ersten von ihnen. Kommt er aus DERSELBEN
    Reihe, schließt sich sein Ursprung: Dann rücken die davor rückwärts, und
    der Spalt liegt an der neuen folgenden Kante des Letzten von ihnen. Bis
    2026-09-04 zählte nur die erste Richtung — wer von ganz links in seine
    eigene Reihe nach rechts trug, sah die Lücke aufgehen, aber keine Marke.
  */
  let forward: number | null = null
  let backward: number | null = null
  const moves = plan.moves.flatMap(move => {
    const from = start.get(move.id)
    // Wer am Ende steht, wo er stand, rührt sich auch im Angebot nicht.
    if (!from || samePlace(move, from)) return []
    // Die reinen Schließer: volle Strecke, und für die **Fuge** zählen sie
    // nicht mit — sie stehen hinter dem Zeiger, nicht in seinem Spalt.
    const shut = shutting.get(move.id)
    if (shut && samePlace(move, shut)) return [{ id: move.id, x: move.x, y: move.y }]
    const step = Math.sign(horizontal ? move.x - from.x : move.y - from.y)
    // Quer zur Achse rückt niemand; ohne Weg auf ihr gibt es nichts zu zeigen.
    if (step === 0) return []
    const view = axisView(from, horizontal)
    if (step > 0) forward = forward === null ? view.lead + opening : Math.min(forward, view.lead + opening)
    else backward = backward === null ? view.trail - opening : Math.max(backward, view.trail - opening)
    return [{
      id: move.id,
      x: horizontal ? from.x + step * opening : from.x,
      y: horizontal ? from.y : from.y + step * opening,
    }]
  })

  if (forward === null && backward === null) return { rect: plan.rect, moves, seam: null }

  /*
    Die Marke steht in der Mitte dessen, was man SIEHT — nicht in der Mitte
    dessen, was aufgeht: Zum Spalt gehört auch die Lücke, die vorher schon da
    war. Ohne sie wäre der Abstand zur einen Seite um einen halben **Takt**
    kleiner als zur anderen (gemeldet 2026-09-04).

    Die andere Kante ist die nächste stehende — in ihrer Lage IM ANGEBOT, denn
    wer den Ursprung schließt, rückt ja gerade. Steht dort niemand (der
    Getragene wird Kopf oder Schluss einer Reihe), bleibt die Kante des
    Weichenden allein.
  */
  const offered = new Map(others.map(entry => [entry.id, entry.rect]))
  for (const move of moves) {
    const from = offered.get(move.id)
    if (from) offered.set(move.id, { ...from, x: move.x, y: move.y })
  }
  const plane = axisView(plan.rect, horizontal)
  const edges: number[] = []
  for (const entry of others) {
    const view = axisView(offered.get(entry.id) ?? entry.rect, horizontal)
    // Quer daneben: liegt in einer anderen Reihe und zählt nicht.
    if (view.crossEnd <= plane.crossStart || view.crossStart >= plane.crossEnd) continue
    edges.push(view.lead, view.trail)
  }
  const before = (at: number) => {
    const found = edges.filter(edge => edge <= at + 0.5)
    return found.length > 0 ? Math.max(...found) : at
  }
  const after = (at: number) => {
    const found = edges.filter(edge => edge >= at - 0.5)
    return found.length > 0 ? Math.min(...found) : at
  }
  const slits = [
    forward === null ? null : { from: before(forward - opening), to: forward },
    backward === null ? null : { from: backward, to: after(backward + opening) },
  ].filter(slit => slit !== null)

  // Bei zwei Spalten (der Ursprung liegt in der Zielreihe) gilt der, der dort
  // liegt, wo der Getragene landet.
  const mitte = (plane.lead + plane.trail) / 2
  const slit = slits.reduce((best, next) =>
    Math.abs((next.from + next.to) / 2 - mitte) < Math.abs((best.from + best.to) / 2 - mitte) ? next : best)

  return {
    rect: plan.rect,
    moves,
    seam: seamOf(plan.rect, target.axis, (slit.from + slit.to) / 2),
  }
}

/** Führende und folgende Kante auf der Reihenachse, dazu die Ausdehnung quer. */
const axisView = (rect: MovingRect, horizontal: boolean) => (horizontal
  ? { lead: rect.x, trail: rect.x + rect.width, crossStart: rect.y, crossEnd: rect.y + rect.height }
  : { lead: rect.y, trail: rect.y + rect.height, crossStart: rect.x, crossEnd: rect.x + rect.width })

const sideOf = (target: ShoveTarget): SpawnSide =>
  target.axis === 'x'
    ? (target.side === 'after' ? 'right' : 'left')
    : (target.side === 'after' ? 'bottom' : 'top')

/** Die Reihe, die sich am Ursprung schließt, und um wie viel sie zurückrückt. */
interface Closing {
  axis: ShoveAxis
  /** Die Strecke, um die jedes Glied zurückrückt — Zettellänge plus Lücke. */
  back: number
  row: InsertEntry[]
  moves: InsertMove[]
}

/**
 * **Das Schließen am Ursprung**: Wer dem Getragenen auf DIESER Achse dicht
 * folgt (`rowAfter`), rückt um die Lücke zurück, die er hinterlässt — seine
 * Länge plus den Abstand zum ersten Nachfolger. Wer stehen bleibt, steht hier
 * nicht.
 *
 * Immer nur EINE Achse: Im Raster rückte sonst Zeile UND Spalte, und die Ecke
 * landete doppelt — die Kettenreaktion, die das Dazwischen anlegen ausschließt.
 * Welche es ist, sagt beim **Einreihen** die Geste (`getShovePlan`), beim
 * **Hinlegen** der Ursprung selbst (`getCloseOriginPlan`).
 *
 * **Und die Reihe endet an der Bereichskante**: Wer zu einem anderen **Bereich**
 * gehört als der Getragene, rückt nicht nach — er hat mit dessen Weggehen
 * nichts zu tun (CONTEXT.md, Bereich; siehe `rowAfter`).
 */
function closingOn(moved: SwapEntry, others: readonly SwapEntry[], axis: ShoveAxis): Closing {
  const horizontal = axis === 'x'
  const origin = viewOf(moved.rect, horizontal)
  const rest = others.map(entry => ({ entry, view: viewOf(entry.rect, horizontal) }))
  // Die Welt des GETRAGENEN: Es schließt sich die Reihe, aus der er kommt —
  // ein fremder **Bereich** dahinter ist eine Wand (`rowAfter`).
  const { entries: row } = rowAfter(origin, rest, lengthOf(moved.rect, axis), worldOf(moved))
  const first = row[0]
  if (!first) return { axis, back: 0, row: [], moves: [] }
  const gap = viewOf(first.rect, horizontal).lead - origin.trail
  const back = lengthOf(moved.rect, axis) + gap
  return {
    axis,
    back,
    row,
    moves: row.map(entry => ({
      id: entry.id,
      x: horizontal ? entry.rect.x - back : entry.rect.x,
      y: horizontal ? entry.rect.y : entry.rect.y - back,
    })),
  }
}

/**
 * Was beim **Hinlegen** in einem **Leerplatz** rückt (CONTEXT.md,
 * Tauschpunkt): am ZIEL niemand — dort ist ja frei —, aber die alte Reihe
 * schließt sich hinter dem Getragenen, wie beim **Dazwischen schieben**.
 *
 * **Warum überhaupt**: Am ENDE einer Reihe gibt es keinen **Rand** (dort
 * rückte niemand); angehängt wird im Feld dahinter. Ohne das Schließen
 * verhielte sich dieselbe Geste am Reihenende also anders als in ihrer Mitte —
 * dazwischen rückte die Herkunftsspalte nach, ans Ende gelegt blieb sie offen
 * (gemeldet 2026-09-04).
 *
 * **Die Achse liest der Ursprung**, denn ein Feld hat keine: Es schließt die
 * Achse, auf der dem Getragenen jemand dicht FOLGT; steht auf beiden jemand
 * (im Raster), die SENKRECHTE — ein Loch in einer Spalte ist das, was man
 * sieht. Folgt niemand, rührt sich nichts.
 *
 * **Gehört das Feld zu der Reihe, die schließt, rückt es mit** — dieselbe
 * Reihen-Regel wie überall: quer zum Ursprung überlappend, hinter dem letzten
 * Weichenden, Lücke höchstens eine Zettellänge. Sonst bliebe zwischen ihm und
 * dem Getragenen eine ganze Zettellänge Luft stehen (Spalte A–B–C, A wandert
 * ans Feld unter C).
 *
 * **Nur in der eigenen Gruppe**: Der Landeplatz muss noch zur **Gruppe** des
 * Getragenen gehören — höchstens eine Zettellänge Luft. Weiter draußen
 * **verschiebt** die Geste, und das Loch bleibt offen (siehe unten).
 *
 * **Und sonst bleibt es liegen, wo es lag.** Nicht „es folgt dem Zettel, an
 * dem es hängt": Ein Feld bieten oft MEHRERE an — das Feld unter dem letzten
 * einer Nachbarspalte hängt genauso an dem Zettel, der links daneben gerade
 * hochrückt. Wäre es dem gefolgt, landete der Getragene auf einem besetzten
 * Platz.
 *
 * ⌘/Strg hält den Ursprung offen; das liest die Geste (`useSwapDrag`) und
 * ruft dann gar nicht erst hier an.
 *
 * @param landing das getroffene Feld, in Canvas-Koordinaten
 */
export function getCloseOriginPlan(entries: SwapEntry[], dragged: string, landing: MovingRect): ShovePlan {
  const moved = entries.find(entry => entry.id === dragged)
  if (!moved) throw new Error(`getCloseOriginPlan: ${dragged} ist nicht unter den Einträgen`)
  const others = entries.filter(entry => entry.id !== dragged)
  const senkrecht = closingOn(moved, others, 'y')
  const closing = senkrecht.moves.length > 0 ? senkrecht : closingOn(moved, others, 'x')
  const last = closing.row[closing.row.length - 1]
  if (!last) return { rect: landing, moves: [] }

  /*
    **Nur in der eigenen Gruppe** (CONTEXT.md, Tauschpunkt): Geschlossen wird
    nur, solange der Landeplatz noch zur **Gruppe** des Getragenen gehören
    würde — höchstens eine Zettellänge Luft zu einem ihrer Zettel. Weiter
    draußen VERSCHIEBT die Geste, und dann bleibt das Loch offen.

    **Gemessen gegen die ganze Gruppe, nicht nur gegen den Ursprung**: Das
    Feld unter C liegt vom Ursprung A aus drei Felder weit weg, von der Gruppe
    aus eines — A ans Ende seiner eigenen Spalte zu tragen ist Umsortieren.

    Ohne diese Grenze schloss sich der Ursprung auch dann, wenn der Zettel im
    Ring einer ganz anderen Gruppe landete: **Leerplätze** liegen rings um
    JEDEN Zettel des Canvas, auch um die außerhalb des Bildes. Gemeldet
    2026-09-05 mit Bild — eine Notiz weit nach links gelegt, und die ganze
    waagerechte Reihe rückte nach, quer zu ihren Spalten.

    Die Geste selbst hält dieselbe Grenze schon ein (`useSwapDrag`): So weit
    draußen wird gar keine **Ableg-Fläche** mehr angeboten. Hier steht sie ein
    zweites Mal, damit die Rechnung für sich stimmt.
  */
  if (!belongsToGroup(landing, groupAround(entries, dragged, moved.rect), moved.rect)) {
    return { rect: landing, moves: [] }
  }

  const horizontal = closing.axis === 'x'
  const origin = viewOf(moved.rect, horizontal)
  const field = viewOf(landing, horizontal)
  const tail = viewOf(last.rect, horizontal)
  const belongs = field.crossEnd > origin.crossStart
    && field.crossStart < origin.crossEnd
    && field.lead >= tail.trail - 0.5
    && field.lead - tail.trail <= lengthOf(moved.rect, closing.axis)

  const rect = belongs
    ? { ...landing, x: horizontal ? landing.x - closing.back : landing.x, y: horizontal ? landing.y : landing.y - closing.back }
    : landing
  return { rect, moves: closing.moves }
}

/**
 * Was beim Loslassen rückt.
 *
 * **Erst schließen, dann öffnen.** Am Ursprung schließt sich die Reihe DIESER
 * Achse — die der Geste (`closingOn`), nur mit `closeOrigin`.
 *
 * Auf der geschlossenen Lage öffnet dann `getInsertPlan` die Zielreihe — hinter
 * dem Bezug (Lücke, Reihenende: um Länge plus Takt, quer auf ihm zentriert)
 * oder vor ihm (Quer-Eintritt: der Bezug rückt selbst mit, oder der Getragene
 * landet im freien Platz davor). Steht ein Zettel in beiden Listen
 * (Umsortieren in derselben Reihe), gilt das Öffnen — es wurde auf der
 * geschlossenen Lage gerechnet.
 */
export function getShovePlan(entries: SwapEntry[], dragged: string, target: ShoveTarget, closeOrigin: boolean): ShovePlan {
  const moved = entries.find(entry => entry.id === dragged)
  if (!moved) throw new Error(`getShovePlan: ${dragged} ist nicht unter den Einträgen`)
  const others = entries.filter(entry => entry.id !== dragged)

  const closing = new Map<string, InsertMove>(
    closeOrigin ? closingOn(moved, others, target.axis).moves.map(move => [move.id, move]) : [],
  )

  // `...entry` und nicht `{ id, rect }`: Die Zugehörigkeit zum **Bereich** und
  // der **Klebestreifen** müssen
  // die geschlossene Lage überleben, sonst stünden alle plötzlich im Freien.
  const closed = others.map(entry => {
    const move = closing.get(entry.id)
    return move ? { ...entry, rect: { ...entry.rect, x: move.x, y: move.y } } : entry
  })
  const anchor = closed.find(entry => entry.id === target.anchor.id)!
  const insert = getInsertPlan(
    anchor,
    sideOf(target),
    { width: moved.rect.width, height: moved.rect.height },
    closed,
    // Beim Schieben gilt die Frei-Regel nicht (siehe `InsertOptions.push`).
    { push: true },
  )

  const moves = new Map(closing)
  for (const move of insert.moves) moves.set(move.id, move)
  return { rect: insert.rect, moves: [...moves.values()] }
}
